
import { useState, useCallback, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { extractDocumentSections, analyzeTopicRelationships } from "@/utils/documentSectionAnalyzer";
import type { Question } from "@/types/quiz";
import { useCurrentAffairsPerformanceAnalysis } from "./useCurrentAffairsPerformanceAnalysis";

export function useCurrentAffairsQuestions(preferredDifficulty = 'medium', selectedDate?: Date) {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { analyzePerformance } = useCurrentAffairsPerformanceAnalysis();
  const [adaptivityEnabled, setAdaptivityEnabled] = useState(true);

  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Format date parameter for query, ensuring time zone issues don't affect date selection
      let formattedDate: string | undefined;
      
      if (selectedDate) {
        const localDate = new Date(selectedDate);
        formattedDate = localDate.toISOString().split('T')[0]; // Get YYYY-MM-DD format
        console.log(`Fetching questions for date: ${formattedDate}`);
      }

      // If adaptivity is enabled, analyze performance to guide question selection
      let adaptiveTopics: string[] = [];
      let userDifficulty = preferredDifficulty;
      
      if (adaptivityEnabled) {
        const performanceData = await analyzePerformance();
        if (performanceData) {
          adaptiveTopics = performanceData.recommendedFocus;
          
          // Adjust difficulty based on overall accuracy
          if (performanceData.overallAccuracy > 85) {
            // If user is doing very well, increase difficulty
            userDifficulty = 'hard';
          } else if (performanceData.overallAccuracy < 50) {
            // If user is struggling, decrease difficulty
            userDifficulty = 'easy';
          }
          
          console.log(`Adaptive mode: Using ${userDifficulty} difficulty based on ${performanceData.overallAccuracy}% accuracy`);
          console.log(`Focusing on topics:`, adaptiveTopics);
        }
      }

      // Fetch current affairs document
      const { data: currentAffairs, error: currentAffairsError } = await supabase
        .from('current_affairs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (currentAffairsError) throw currentAffairsError;

      // Extract and analyze document sections
      const sections = await extractDocumentSections(
        currentAffairs.id, 
        currentAffairs.content
      );

      // Analyze topic relationships
      await analyzeTopicRelationships(currentAffairs.id, sections);

      // Fetch questions, now potentially linked to document sections
      let questionsQuery = supabase
        .from('current_affairs_questions')
        .select('*');
      
      // Apply date filter
      questionsQuery = questionsQuery.eq('date', formattedDate || currentAffairs.date);
      
      // Apply adaptive filters if available
      if (adaptivityEnabled && adaptiveTopics.length > 0) {
        // Prioritize questions from weak topics, but don't exclusively select them
        // We'll get more questions and then sort/filter later
        questionsQuery = questionsQuery.limit(50); // Get more questions than we need
      } else {
        // Apply difficulty filter
        questionsQuery = questionsQuery.eq('difficulty', userDifficulty);
      }
      
      const { data: fetchedQuestions, error: questionsError } = await questionsQuery;

      if (questionsError) throw questionsError;

      // If we don't have enough questions of the preferred difficulty, fetch any difficulty
      let allQuestions = [...(fetchedQuestions || [])];
      
      if (allQuestions.length < 20) {
        const { data: additionalQuestions, error: additionalError } = await supabase
          .from('current_affairs_questions')
          .select('*')
          .eq('date', formattedDate || currentAffairs.date)
          .neq('difficulty', userDifficulty);
          
        if (!additionalError && additionalQuestions) {
          allQuestions = [...allQuestions, ...additionalQuestions];
        }
      }

      // Apply adaptive sorting if enabled and we have topics to focus on
      if (adaptivityEnabled && adaptiveTopics.length > 0) {
        // Sort questions to prioritize those from weak topics
        allQuestions.sort((a, b) => {
          const aInWeakTopic = adaptiveTopics.includes(a.time_period);
          const bInWeakTopic = adaptiveTopics.includes(b.time_period);
          
          if (aInWeakTopic && !bInWeakTopic) return -1;
          if (!aInWeakTopic && bInWeakTopic) return 1;
          
          // For questions in same category, sort by difficulty
          if (userDifficulty === 'hard') {
            // Prioritize harder questions
            return (b.difficulty === 'hard' ? 1 : 0) - (a.difficulty === 'hard' ? 1 : 0);
          } else {
            // Prioritize easier questions
            return (a.difficulty === 'easy' ? 1 : 0) - (b.difficulty === 'easy' ? 1 : 0);
          }
        });
      }
      
      // Randomize the questions and limit to 20
      const randomizedQuestions = adaptivityEnabled 
        ? allQuestions.slice(0, 20) // Take first 20 after adaptive sorting
        : allQuestions.sort(() => 0.5 - Math.random()).slice(0, 20);

      // Transform the fetched questions to match the expected format for QuestionCard
      const transformedQuestions = randomizedQuestions.map(q => {
        // Create options array from individual option fields
        const options = [q.option_a, q.option_b, q.option_c, q.option_d].filter(Boolean);
        
        // Find index of correct answer in the options array
        let correctAnswerIndex = options.findIndex(opt => 
          opt?.toLowerCase().trim() === q.correct_answer?.toLowerCase().trim()
        );
        
        // If the correct answer wasn't found, default to the first option
        if (correctAnswerIndex === -1) correctAnswerIndex = 0;
        
        return {
          id: q.id,
          question: q.question,
          options: options,
          correctanswer: correctAnswerIndex,
          difficulty: q.difficulty as "easy" | "medium" | "hard",
          category: "Current Affairs",
          subject: "Current Affairs",
          explanation: q.explanation || '',
          hint: q.explanation || '',
          question_type: 'mcq' as const
        } as Question;
      });

      console.log(`Transformed ${transformedQuestions.length} current affairs questions with adaptive mode: ${adaptivityEnabled}`);
      setQuestions(transformedQuestions);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [preferredDifficulty, selectedDate, adaptivityEnabled, analyzePerformance]);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  const refreshQuestions = useCallback(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  const toggleAdaptivity = useCallback(() => {
    setAdaptivityEnabled(prev => !prev);
  }, []);

  return {
    questions,
    loading,
    error,
    refreshQuestions,
    adaptivityEnabled,
    toggleAdaptivity
  };
}
